import type { Class } from '../../types/tournament';
import type { IMatchScheduleItem } from '../../types/schedule';
import { isBoxLeagueClass, MatchScheduleInterval } from './scheduleConflictDetector';

export function normalizeBoxLeagueRound(match: IMatchScheduleItem): number | null {
  const n = Number(match.type);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

function parseBoxIndexFromMatchId(matchId: string | undefined): number | null {
  if (!matchId) return null;
  const match = /^BL-BX(\d+)-LR/i.exec(matchId);
  if (!match) return null;
  const n = Number(match[1]);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

function parseBoxIndexFromGroupName(name: string | undefined): number | null {
  if (!name) return null;
  const m = /Box\s*(\d+)/i.exec(name);
  if (!m) return null;
  const n = Number(m[1]);
  return Number.isFinite(n) && n > 0 ? Math.floor(n) : null;
}

export function resolveBoxIndex(
  match: IMatchScheduleItem,
  groupIdToBoxIndex?: ReadonlyMap<number, number>
): number | null {
  const fromGroup = match.tournamentGroup?.boxIndex;
  if (fromGroup != null) {
    const n = Number(fromGroup);
    if (Number.isFinite(n) && n > 0) return Math.floor(n);
  }
  const fromMatchId = parseBoxIndexFromMatchId(match.matchId);
  if (fromMatchId != null) return fromMatchId;
  const groupId = match.tournamentGroup?.id;
  if (groupId != null && groupIdToBoxIndex?.has(groupId)) {
    return groupIdToBoxIndex.get(groupId)!;
  }
  return parseBoxIndexFromGroupName(match.tournamentGroup?.name);
}

export function buildGroupIdToBoxIndexMap(
  matches: Iterable<IMatchScheduleItem>
): Map<number, number> {
  const map = new Map<number, number>();
  const list = [...matches];
  for (const match of list) {
    const groupId = match.tournamentGroup?.id;
    if (groupId == null) continue;
    const boxIndex = resolveBoxIndex(match);
    if (boxIndex != null && !map.has(groupId)) map.set(groupId, boxIndex);
  }
  for (const match of list) {
    const groupId = match.tournamentGroup?.id;
    if (groupId == null || map.has(groupId)) continue;
    const boxIndex = resolveBoxIndex(match, map);
    if (boxIndex != null) map.set(groupId, boxIndex);
  }
  return map;
}

export function getBoxScopeKey(
  interval: MatchScheduleInterval,
  classObject: Class | undefined,
  groupIdToBoxIndex?: ReadonlyMap<number, number>
): string | null {
  if (!classObject || !isBoxLeagueClass(classObject)) return null;
  const classId = interval.match.classId;
  if (classObject.moveBox) {
    const boxIndex = resolveBoxIndex(interval.match, groupIdToBoxIndex);
    if (boxIndex != null) return `${classId}:bx${boxIndex}`;
    return `${classId}:0`;
  }
  const groupId = interval.match.tournamentGroup?.id ?? 0;
  return `${classId}:${groupId}`;
}

export function boxRoundOrderingViolation(
  a: MatchScheduleInterval,
  b: MatchScheduleInterval,
  classObject: Class | undefined,
  groupIdToBoxIndex?: ReadonlyMap<number, number>
): boolean {
  return boxRoundOrderingViolationForClass(a, b, classObject, groupIdToBoxIndex);
}

export function boxRoundOrderingViolationForClass(
  a: MatchScheduleInterval,
  b: MatchScheduleInterval,
  classObject: Class | undefined,
  groupIdToBoxIndex?: ReadonlyMap<number, number>
): boolean {
  if (!classObject || !isBoxLeagueClass(classObject)) return false;
  if (a.match.classId !== b.match.classId) return false;
  if (!classObject.moveBox) {
    const keyA = getBoxScopeKey(a, classObject, groupIdToBoxIndex);
    const keyB = getBoxScopeKey(b, classObject, groupIdToBoxIndex);
    if (keyA == null || keyA !== keyB) return false;
  }
  const rA = normalizeBoxLeagueRound(a.match);
  const rB = normalizeBoxLeagueRound(b.match);
  if (rA == null || rB == null || rA === rB) return false;
  if (a.dayKey !== b.dayKey) return false;
  if (rB < rA && a.startMs < b.endMs && b.startMs < a.endMs) return true;
  if (rB > rA && b.startMs < a.endMs && a.startMs < b.endMs) return true;
  return false;
}

function boxRoundOrderPeers(
  pending: MatchScheduleInterval,
  peers: MatchScheduleInterval[],
  classObject: Class | undefined,
  groupIdToBoxIndex?: ReadonlyMap<number, number>
): MatchScheduleInterval[] {
  const sameClass = peers.filter(
    (p) => p.match.id !== pending.match.id && p.match.classId === pending.match.classId
  );
  if (classObject?.moveBox) return sameClass;
  const scope = getBoxScopeKey(pending, classObject, groupIdToBoxIndex);
  if (scope == null) return [];
  return sameClass.filter(
    (p) => getBoxScopeKey(p, classObject, groupIdToBoxIndex) === scope
  );
}

type RoundPhaseBounds = {
  maxEndByRound: Map<number, number>;
  minStartByRound: Map<number, number>;
  maxEndIntervalByRound: Map<number, MatchScheduleInterval>;
  minStartIntervalByRound: Map<number, MatchScheduleInterval>;
};

function roundPhaseBounds(bucket: MatchScheduleInterval[]): RoundPhaseBounds | null {
  const maxEndByRound = new Map<number, number>();
  const minStartByRound = new Map<number, number>();
  const maxEndIntervalByRound = new Map<number, MatchScheduleInterval>();
  const minStartIntervalByRound = new Map<number, MatchScheduleInterval>();

  for (const interval of bucket) {
    const r = normalizeBoxLeagueRound(interval.match);
    if (r == null) continue;
    const prevMax = maxEndByRound.get(r);
    if (prevMax == null || interval.endMs > prevMax) {
      maxEndByRound.set(r, interval.endMs);
      maxEndIntervalByRound.set(r, interval);
    }
    const prevMin = minStartByRound.get(r);
    if (prevMin == null || interval.startMs < prevMin) {
      minStartByRound.set(r, interval.startMs);
      minStartIntervalByRound.set(r, interval);
    }
  }
  if (maxEndByRound.size === 0) return null;
  return {
    maxEndByRound,
    minStartByRound,
    maxEndIntervalByRound,
    minStartIntervalByRound,
  };
}

export type BoxRoundPhaseViolation = {
  rLow: number;
  rHigh: number;
  earlierMatchId: number;
  laterMatchId: number;
  classId: number;
  groupId: number;
};

export function findBoxRoundPhaseViolation(
  bucket: MatchScheduleInterval[],
  classObject: Class | undefined
): BoxRoundPhaseViolation | null {
  if (!isBoxLeagueClass(classObject)) return null;
  const bounds = roundPhaseBounds(bucket);
  if (!bounds) return null;

  const rounds = [...bounds.maxEndByRound.keys()].sort((a, b) => a - b);
  for (const rLow of rounds) {
    for (const rHigh of rounds) {
      if (rHigh <= rLow) continue;
      const maxEnd = bounds.maxEndByRound.get(rLow)!;
      const minStart = bounds.minStartByRound.get(rHigh)!;
      if (minStart < maxEnd) {
        const earlier = bounds.maxEndIntervalByRound.get(rLow)!;
        const later = bounds.minStartIntervalByRound.get(rHigh)!;
        return {
          rLow,
          rHigh,
          earlierMatchId: earlier.match.id,
          laterMatchId: later.match.id,
          classId: earlier.match.classId,
          groupId: earlier.match.tournamentGroup?.id ?? 0,
        };
      }
    }
  }
  return null;
}

export function boxRoundPhaseStableId(
  scopeKey: string,
  rLow: number,
  rHigh: number
): string {
  return `BOX_ROUND_ORDERING-${scopeKey.replace(':', '-')}-${rLow}-${rHigh}`;
}

export function wouldViolateBoxRoundPhaseOnPlace(
  pending: MatchScheduleInterval,
  peers: MatchScheduleInterval[],
  classObject: Class | undefined,
  groupIdToBoxIndex?: ReadonlyMap<number, number>
): boolean {
  if (!isBoxLeagueClass(classObject)) return false;
  const pendingRound = normalizeBoxLeagueRound(pending.match);
  if (pendingRound == null) return false;

  const scope = getBoxScopeKey(pending, classObject, groupIdToBoxIndex);
  if (scope == null) return false;

  const sameScope = boxRoundOrderPeers(pending, peers, classObject, groupIdToBoxIndex);

  let result = false;
  for (const other of sameScope) {
    if (boxRoundOrderingViolationForClass(pending, other, classObject, groupIdToBoxIndex)) {
      result = true;
      break;
    }
  }

  let maxEndLower = -Infinity;
  if (!result) {
    for (const other of sameScope) {
      const otherRound = normalizeBoxLeagueRound(other.match);
      if (otherRound == null || otherRound >= pendingRound) continue;
      if (other.endMs > maxEndLower) maxEndLower = other.endMs;
    }
    if (maxEndLower !== -Infinity && pending.startMs < maxEndLower) result = true;
  }

  let minStartHigher = Infinity;
  if (!result) {
    for (const other of sameScope) {
      const otherRound = normalizeBoxLeagueRound(other.match);
      if (otherRound == null || otherRound <= pendingRound) continue;
      if (other.startMs < minStartHigher) minStartHigher = other.startMs;
    }
    if (minStartHigher !== Infinity && pending.endMs > minStartHigher) result = true;
  }

  return result;
}

export function buildBoxScopeBuckets(
  intervals: MatchScheduleInterval[],
  classIdToObject: Record<string, Class>,
  groupIdToBoxIndex?: ReadonlyMap<number, number>
): Map<string, { bucket: MatchScheduleInterval[]; classObject: Class }> {
  const buckets = new Map<string, { bucket: MatchScheduleInterval[]; classObject: Class }>();

  for (const interval of intervals) {
    const classObject =
      classIdToObject[String(interval.match.classId)] ??
      classIdToObject[interval.match.classId];
    const key = getBoxScopeKey(interval, classObject, groupIdToBoxIndex);
    if (key == null) continue;
    const entry = buckets.get(key) ?? { bucket: [], classObject: classObject! };
    entry.bucket.push(interval);
    buckets.set(key, entry);
  }

  return buckets;
}

export function boxLeagueIntervalsViolateRoundOrder(
  intervals: MatchScheduleInterval[],
  classIdToObject: Record<string, Class>,
  groupIdToBoxIndex?: ReadonlyMap<number, number>
): boolean {
  const byClassId = new Map<number, MatchScheduleInterval[]>();
  for (const interval of intervals) {
    const list = byClassId.get(interval.match.classId) ?? [];
    list.push(interval);
    byClassId.set(interval.match.classId, list);
  }

  for (const [classId, classIntervals] of byClassId) {
    const classObject =
      classIdToObject[String(classId)] ?? classIdToObject[classId];
    if (!isBoxLeagueClass(classObject)) continue;

    if (classObject.moveBox) {
      if (findBoxRoundPhaseViolation(classIntervals, classObject)) return true;
      for (let i = 0; i < classIntervals.length; i++) {
        for (let j = i + 1; j < classIntervals.length; j++) {
          if (
            boxRoundOrderingViolationForClass(
              classIntervals[i],
              classIntervals[j],
              classObject,
              groupIdToBoxIndex
            )
          ) {
            return true;
          }
        }
      }
      continue;
    }

    const buckets = buildBoxScopeBuckets(classIntervals, classIdToObject, groupIdToBoxIndex);
    for (const { bucket, classObject: bucketClass } of buckets.values()) {
      if (findBoxRoundPhaseViolation(bucket, bucketClass)) return true;
      for (let i = 0; i < bucket.length; i++) {
        for (let j = i + 1; j < bucket.length; j++) {
          if (
            boxRoundOrderingViolationForClass(
              bucket[i],
              bucket[j],
              bucketClass,
              groupIdToBoxIndex
            )
          ) {
            return true;
          }
        }
      }
    }
  }
  return false;
}
